import { IncidentCriteria } from '@src/main/modules/other_incidents/domain/incident.model';
import { toEndOfDayUTC, toStartOfDayUTC } from '@src/main/shared/utils/dateUtils';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTableFilterMeta } from 'primereact/datatable';

export type IncidentFilterMeta = DataTableFilterMeta & {
  global: {
    value: string | null;
    matchMode: FilterMatchMode;
  };

  flightDate: {
    operator: FilterOperator;
    constraints: Array<{
      value: [Date | null, Date | null];
      matchMode: FilterMatchMode.BETWEEN;
    }>;
  };

  incidentDate: {
    operator: FilterOperator;
    constraints: Array<{
      value: [Date | null, Date | null];
      matchMode: FilterMatchMode.BETWEEN;
    }>;
  };

  updatedAt: {
    operator: FilterOperator;
    constraints: Array<{
      value: [Date | null, Date | null] | null;
      matchMode: FilterMatchMode.BETWEEN;
    }>;
  };

  flightNumber: {
    operator: FilterOperator;
    constraints: Array<{
      value: string | null;
      matchMode: FilterMatchMode;
    }>;
  };

  departure: {
    operator: FilterOperator;
    constraints: Array<{
      value: string | null;
      matchMode: FilterMatchMode;
    }>;
  };

  arrival: {
    operator: FilterOperator;
    constraints: Array<{
      value: string | null;
      matchMode: FilterMatchMode;
    }>;
  };

  employeeId: {
    operator: FilterOperator;
    constraints: Array<{
      value: string | null;
      matchMode: FilterMatchMode;
    }>;
  };
  comment: {
    operator: FilterOperator;
    constraints: Array<{
      value: string | null;
      matchMode: FilterMatchMode;
    }>;
  };
  appVersion: {
    operator: FilterOperator;
    constraints: Array<{
      value: string | null;
      matchMode: FilterMatchMode;
    }>;
  };

  state: {
    operator: FilterOperator;
    constraints: Array<{
      value: string | null;
      matchMode: FilterMatchMode;
    }>;
  };
  linkColumbus: {
    operator: FilterOperator;
    constraints: Array<{
      value: string | null;
      matchMode: FilterMatchMode;
    }>;
  };
};

export const mapIncidentFiltersToCriteria = (
  filters: IncidentFilterMeta,
  base: IncidentCriteria,
): IncidentCriteria => {
  const criteria: IncidentCriteria = { ...base };

  const normalize = (value?: string | null) =>
    typeof value === 'string' ? value.toUpperCase().trim() : value;

  const fieldMap: Record<
    | 'flightNumber'
    | 'departure'
    | 'arrival'
    | 'employeeId'
    | 'appVersion'
    | 'state'
    | 'linkColumbus'
    | 'comment',
    keyof IncidentCriteria
  > = {
    flightNumber: 'flightNumber',
    departure: 'departure',
    arrival: 'arrival',
    employeeId: 'employeeId',
    appVersion: 'appVersion',
    state: 'state',
    linkColumbus: 'linkColumbus',
    comment: 'comment',
  };

  Object.keys(fieldMap).forEach((filterKey) => {
    const criteriaKey = fieldMap[filterKey as keyof typeof fieldMap];
    const filter = filters[filterKey as keyof IncidentFilterMeta];

    const value = filter && 'constraints' in filter ? filter.constraints?.[0]?.value : null;

    if (value) {
      if (criteriaKey === 'appVersion') {
        (criteria[criteriaKey] as string) = (value as string).trim();
      } else {
        (criteria[criteriaKey] as string) = normalize(value) as string;
      }
    }
  });

  const flightDateRange = filters.flightDate?.constraints?.[0]?.value;
  const updatedAtRange = filters.updatedAt?.constraints?.[0]?.value;

  if (flightDateRange && Array.isArray(flightDateRange)) {
    const [start, end] = flightDateRange;

    if (start) criteria.startDate = new Date(toStartOfDayUTC(start));
    if (end) criteria.endDate = new Date(toEndOfDayUTC(end));
  } else if (
    updatedAtRange &&
    Array.isArray(updatedAtRange) &&
    (updatedAtRange[0] || updatedAtRange[1])
  ) {
    const [start, end] = updatedAtRange;

    if (start) criteria.startDate = new Date(toStartOfDayUTC(start));
    if (end) criteria.endDate = new Date(toEndOfDayUTC(end));
  }

  const incidentDateRange = filters.incidentDate?.constraints?.[0]?.value;

  if (incidentDateRange && Array.isArray(incidentDateRange)) {
    const [start, end] = incidentDateRange;

    if (start) criteria.startIncidentDate = new Date(toStartOfDayUTC(start));
    if (end) criteria.endIncidentDate = new Date(toEndOfDayUTC(end));
  }

  return criteria;
};
